"use client"

import { useState, useEffect, Suspense } from "react"
import { useAuth } from "@/lib/auth/context"
import { useSearchParams, useRouter } from "next/navigation"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { FriendsExplore } from "@/components/friends-explore"
import { SurfPartiesExplore } from "@/components/surf-parties-explore"
import { MemberSessionsExplore } from "@/components/member-sessions-explore"
import { SurfSpotGrid } from "@/components/surf-spot-grid"
import { MapView } from "@/components/map-view"
import { FilterSidebar, FilterState } from "@/components/filter-sidebar"
import { SpotsToolbar } from "@/components/spots-toolbar"
import { PreferencesButton } from "@/components/preferences-button"
import { ErrorBoundary } from "@/components/ui/error-boundary"
import { usePlatform, triggerHaptic } from "@/hooks/use-platform"
import { Grid, Users, PartyPopper, Waves, Compass, Shield } from "lucide-react"
import { ThemeToggle } from "@/components/theme-toggle"
import { AppLoadingShell } from "@/components/app-loading-shell"
import { cn } from "@/lib/utils"

import { AuthWall } from "@/components/auth-wall"
import { createClient } from "@/lib/supabase/client"

function ExploreContent() {
  const { isMobile, isLoaded } = usePlatform()
  const searchParams = useSearchParams()
  const router = useRouter()
  const { user, loading } = useAuth()
  
  const [activeTab, setActiveTab] = useState<string>("spots")
  const [viewMode, setViewMode] = useState<"grid" | "map">("grid")
  const [showMobileFilters, setShowMobileFilters] = useState(false)
  const [appliedFilters, setAppliedFilters] = useState<FilterState | null>(null)
  const [userProfile, setUserProfile] = useState<any>(null)

  useEffect(() => {
    if (isLoaded) {
      const tab = searchParams.get("tab")
      if (tab && ["spots", "friends", "parties", "sessions", "admin"].includes(tab)) {
        setActiveTab(tab)
      }
    }
  }, [isLoaded, searchParams])

  useEffect(() => {
    if (user) {
      const fetchProfile = async () => {
        const supabase = createClient()
        const { data } = await supabase.from("users").select("role").eq("auth_id", user.id).single()
        setUserProfile(data)
      }
      fetchProfile()
    }
  }, [user])

  const isAdmin = userProfile?.role === "admin" || user?.email === "contact@spotstosurf.com"

  const handleFiltersApply = (filters: FilterState) => {
    setAppliedFilters(filters)
  }

  const handleTabChange = (tabId: string) => {
    triggerHaptic('selection')
    setActiveTab(tabId)
    const url = new URL(window.location.href)
    url.searchParams.set("tab", tabId)
    window.history.replaceState({}, "", url.toString())
  }

  if (loading) {
    return <AppLoadingShell />
  }

  // MOBILE VIEW
  if (isMobile) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 pb-24 flex flex-col relative pt-[calc(64px+env(safe-area-inset-top))]">
        <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
          <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-blue-500/5 blur-[120px] rounded-full" />
        </div>

        <div className="sticky top-[calc(64px+env(safe-area-inset-top))] z-40 bg-white/80 dark:bg-slate-950/80 backdrop-blur-2xl border-b border-slate-200/50 dark:border-slate-800/50">
          <div className="grid grid-cols-4 w-full h-16">
            {[
              { id: "spots", label: "Spots", icon: Grid },
              { id: "friends", label: "Friends", icon: Users },
              { id: "parties", label: "Parties", icon: PartyPopper },
              { id: "sessions", label: "Sessions", icon: Waves }
            ].map((tab) => (
              <button 
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={cn(
                  "relative h-full flex flex-col items-center justify-center gap-1 transition-all group",
                  activeTab === tab.id ? "text-blue-600 dark:text-blue-400" : "text-slate-400 dark:text-slate-500"
                )}
              >
                <tab.icon className={cn("w-5 h-5 transition-transform group-active:scale-90", activeTab === tab.id ? "scale-110" : "")} />
                <span className="font-black uppercase text-[8px] tracking-widest italic">{tab.label}</span>
                {activeTab === tab.id && (
                  <div className="absolute bottom-0 left-1/4 right-1/4 h-0.5 bg-blue-600 dark:bg-blue-400 rounded-full" />
                )}
              </button>
            ))}
          </div>
        </div>

        <div className="px-6 pt-4 pb-10 flex-1 relative z-10">
          <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
            {activeTab === "spots" && <SurfSpotGrid />}
            {activeTab === "friends" && (
              user ? <FriendsExplore /> : <AuthWall title="Connect with Friends" description="Join the community to see where your surf buddies are planning to go." />
            )}
            {activeTab === "parties" && (
              user ? <SurfPartiesExplore /> : <AuthWall title="Join Surf Parties" description="Discover, organize and participate in surf events." />
            )}
            {activeTab === "sessions" && (
              user ? <MemberSessionsExplore /> : <AuthWall title="Track Your Sessions" description="Keep a digital log of all your sessions." />
            )}
          </div>
        </div>
      </div>
    )
  }

  // DESKTOP VIEW
  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 text-foreground flex flex-col relative">
      <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-blue-500/10 blur-[120px] rounded-full" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-teal-500/10 blur-[120px] rounded-full" />
      </div>

      <div className="relative z-10 flex flex-col flex-1">
        {/* Header Spacer */}
        <div className="h-24 md:h-32 shrink-0" />

        <div className="container mx-auto px-6 max-w-7xl">
          {/* Main Desktop Navigation Tabs */}
          <div className="flex flex-col md:flex-row items-center justify-between mb-12 gap-8">
            <div className="flex flex-col">
              <h1 className="text-4xl md:text-5xl font-black text-slate-900 dark:text-white uppercase tracking-tighter italic leading-none">Explore</h1>
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-2 italic flex items-center gap-2">
                <Compass className="w-3 h-3 text-blue-500" />
                Worldwide Surf & Kite Community
              </p>
            </div>

            <div className="flex items-center gap-1.5 bg-white/50 dark:bg-slate-900/50 backdrop-blur-xl p-1.5 rounded-2xl border border-white/20 dark:border-slate-800 shadow-xl">
              {[
                { id: "spots", label: "Spots", icon: Grid },
                { id: "friends", label: "Friends", icon: Users },
                { id: "parties", label: "Parties", icon: PartyPopper },
                { id: "sessions", label: "Sessions", icon: Waves },
                ...(isAdmin ? [{ id: "admin", label: "Admin Panel", icon: Shield }] : [])
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={cn(
                    "flex items-center gap-2 px-6 py-3 rounded-xl transition-all font-black uppercase tracking-widest text-[10px] italic",
                    activeTab === tab.id
                      ? "bg-white dark:bg-slate-700 text-blue-600 dark:text-white shadow-xl scale-[1.05]"
                      : "text-slate-400 hover:text-slate-900 dark:hover:text-slate-200"
                  )}
                >
                  <tab.icon className="w-4 h-4" />
                  <span className="hidden lg:inline">{tab.label}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="flex gap-8 relative pb-20">
            {activeTab === "spots" && (
              <>
                <FilterSidebar 
                  showMobileFilters={showMobileFilters} 
                  setShowMobileFilters={setShowMobileFilters}
                  onFiltersApply={handleFiltersApply}
                />
                
                <div className={cn(
                  "flex-1 transition-all duration-500",
                  showMobileFilters ? "lg:ml-80" : ""
                )}>
                  <div className="mb-8">
                    <SpotsToolbar
                      viewMode={viewMode}
                      onViewModeChange={setViewMode}
                      onFilterToggle={() => setShowMobileFilters(!showMobileFilters)}
                      showFilters={showMobileFilters}
                    />
                  </div>

                  <div className={cn(
                    "transition-all duration-500",
                    viewMode === "map" ? "h-[70vh] rounded-[3rem] overflow-hidden shadow-2xl border border-white/20 dark:border-slate-800" : ""
                  )}>
                    <ErrorBoundary fallback={<div className="p-8 bg-red-50 text-red-500 rounded-3xl">Error loading grid</div>}>
                      {viewMode === "grid" ? (
                        <div className="animate-in fade-in slide-in-from-bottom-4 duration-700">
                          <SurfSpotGrid />
                        </div>
                      ) : (
                        <div className="h-full w-full">
                          <MapView />
                        </div>
                      )}
                    </ErrorBoundary>
                  </div>
                </div>
              </>
            )}

            {activeTab !== "spots" && (
              <div className="w-full animate-in fade-in slide-in-from-bottom-4 duration-700">
                {activeTab === "friends" && (
                  user ? <FriendsExplore /> : <AuthWall title="Connect with Friends" description="Join the community to see where your surf buddies are planning to go." />
                )}
                {activeTab === "parties" && (
                  user ? <SurfPartiesExplore /> : <AuthWall title="Join Surf Parties" description="Discover, organize and participate in surf events." />
                )}
                {activeTab === "sessions" && (
                  user ? <MemberSessionsExplore /> : <AuthWall title="Track Your Sessions" description="Keep a digital log of all your sessions." />
                )}
                {activeTab === "admin" && isAdmin && (
                  <div className="p-10 bg-white/50 dark:bg-slate-900/50 backdrop-blur-xl rounded-[3rem] border border-white/20 dark:border-slate-800 text-center">
                    <Shield className="w-16 h-16 text-blue-500 mx-auto mb-6" />
                    <h2 className="text-3xl font-black uppercase tracking-tighter italic text-slate-900 dark:text-white mb-4">Admin Command Center</h2>
                    <p className="font-bold text-slate-500 uppercase tracking-widest mb-8">Access protected management tools and system oversight.</p>
                    <button 
                      onClick={() => router.push("/admin")}
                      className="h-16 px-12 bg-blue-600 hover:bg-blue-500 text-white rounded-2xl font-black uppercase tracking-widest text-xs shadow-xl shadow-blue-600/30 transition-all active:scale-95"
                    >
                      Enter Admin Portal
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default function ExplorePage() {
  return (
    <Suspense fallback={<AppLoadingShell />}>
      <ExploreContent />
    </Suspense>
  )
}
